import { apiClient } from "@/shared/api/client"
import { presignUpload } from "@/shared/api/media-upload"
import { uploadFileToS3 } from "@/shared/api/upload-to-s3"
import type { Media } from "@/shared/lib/types"
import type { AdminPost, Platform, PostStats, Surface, SurfacePlacement } from "../model/types"

/** Query params for fetching the paginated admin posts list. */
export interface AdminPostsParams {
	search?: string
	page?: number
	limit?: number
	status?: "all" | "published" | "draft" | "deleted"
	/** Площадка, к которой относится published или draft. */
	platform?: Platform
}

/** Paginated response returned by {@link getAdminPosts}. */
export interface AdminPostsResponse {
	posts: AdminPost[]
	total: number
	page: number
	limit: number
}

/** Payload for creating a new post via {@link createAdminPost}. */
export interface CreatePostPayload {
	description?: string
	isActive: boolean
	title?: string
	shortDescription?: string
	formatIds?: string
	nicheIds?: string
	topicIds?: string
	durationIds?: string
}

/** Payload for updating an existing post via {@link updateAdminPost}. */
export interface UpdatePostPayload {
	id: string
	description?: string
	isActive?: boolean
	title?: string
	shortDescription?: string
	formatIds?: string[]
	nicheIds?: string[]
	topicIds?: string[]
	durationIds?: string[]
}

/** Fetches a paginated list of posts for the admin panel, optionally filtered by status. */
export const getAdminPosts = async (params?: AdminPostsParams): Promise<AdminPostsResponse> => {
	const queryParams: Record<string, string | number> = {
		page: params?.page || 1,
		...(params?.search ? { search: params.search } : {}),
		limit: params?.limit || 20,
	}

	if (params?.status && params.status !== "all") {
		queryParams.status = params.status
		if (params.platform && params.status !== "deleted") queryParams.platform = params.platform
	}

	const response = await apiClient.get<AdminPostsResponse>("/admin/posts", {
		params: queryParams,
	})
	return response.data
}

/** Fetches a single post by id for the admin panel. */
export const getAdminPost = async (id: string): Promise<AdminPost> => {
	const response = await apiClient.get<AdminPost>(`/admin/posts/${id}`)
	return response.data
}

/** Creates a new post. */
export const createAdminPost = async (payload: CreatePostPayload): Promise<AdminPost> => {
	const response = await apiClient.post<AdminPost>("/admin/posts", payload)
	return response.data
}

/** Updates an existing post's fields. */
export const updateAdminPost = async (payload: UpdatePostPayload): Promise<AdminPost> => {
	const { id, ...updateData } = payload
	const response = await apiClient.patch<AdminPost>(`/admin/posts/${id}`, updateData)
	return response.data
}

/** Soft-deletes a post. */
export const deleteAdminPost = async (id: string): Promise<void> => {
	await apiClient.delete(`/admin/posts/${id}`)
}

/** Restores a previously soft-deleted post. */
export const restoreAdminPost = async (id: string): Promise<AdminPost> => {
	const response = await apiClient.post<AdminPost>(`/admin/posts/${id}/restore`)
	return response.data
}

/** Просмотры, читатели, избранное и досмотр видео одного поста. */
export const getPostStats = async (id: string): Promise<PostStats> =>
	(await apiClient.get<PostStats>(`/admin/posts/${id}/stats`)).data

/** Публикует пост в приложении или снимает его оттуда. */
export const setPostSurface = async (
	postId: string,
	surface: Surface,
	status: "VISIBLE" | "HIDDEN",
): Promise<SurfacePlacement> =>
	(
		await apiClient.patch<SurfacePlacement>(`/admin/posts/${postId}/surface-status`, {
			surface,
			status,
		})
	).data

/** Открывает пост тем, кто не в клубе, или закрывает; отвечает числом открытых постов. */
export const setPostFreeAccess = async (
	postId: string,
	enabled: boolean,
): Promise<{ freeAccess: boolean; total: number }> =>
	(
		await apiClient.patch<{ freeAccess: boolean; total: number }>(
			`/admin/posts/${postId}/free-access`,
			{ enabled },
		)
	).data

/** Добавляет пост в подборку для ревью App Store или убирает; отвечает размером подборки. */
export const setPostReviewShowcase = async (
	postId: string,
	enabled: boolean,
): Promise<{ reviewShowcase: boolean; total: number }> =>
	(
		await apiClient.patch<{ reviewShowcase: boolean; total: number }>(
			`/admin/posts/${postId}/review-showcase`,
			{ enabled },
		)
	).data

interface PublishResponse {
	success: boolean
	message: string
	jobId?: string
	scheduledAt?: string
}

/** Публикует пост в канал: без channelId API берёт канал клуба из своих настроек. */
export const publishAdminPost = async (
	postId: string,
	data: { channelId?: string; scheduledAt?: string },
): Promise<PublishResponse> =>
	(await apiClient.post<PublishResponse>(`/admin/posts/${postId}/publish`, data)).data

export interface PublishStatus {
	state: string
	progress: number
	message?: string
	result?: {
		success: boolean
		postUrl?: string
		publication: { id: string; messageId?: string | null }
	}
	error?: string
}

/** Polls the status of an in-progress publish job. */
export const getPublishStatus = async (postId: string, jobId: string): Promise<PublishStatus> =>
	(await apiClient.get<PublishStatus>(`/admin/posts/${postId}/publish-status/${jobId}`)).data

/** Cancels a scheduled (not-yet-published) publication. */
export const cancelScheduledPublication = async (postId: string, publicationId: string) =>
	(await apiClient.post(`/admin/posts/${postId}/publications/${publicationId}/cancel`)).data

/** Удаляет сообщение поста из канала. */
export const unpublishFromChannel = async (postId: string, publicationId: string) =>
	(await apiClient.post(`/admin/posts/${postId}/publications/${publicationId}/unpublish`)).data

/** Снимает пост с канала и отправляет заново: нужно, когда правкой альбом не обновить. */
export const republishToChannel = async (
	postId: string,
	publicationId: string,
): Promise<PublishResponse> =>
	(
		await apiClient.post<PublishResponse>(
			`/admin/posts/${postId}/publications/${publicationId}/republish`,
		)
	).data

/** Повторяет публикацию, которая не удалась. */
export const retryChannelPublication = async (
	postId: string,
	publicationId: string,
): Promise<PublishResponse> =>
	(
		await apiClient.post<PublishResponse>(
			`/admin/posts/${postId}/publications/${publicationId}/retry`,
		)
	).data

/** Переносит правки поста в уже отправленное сообщение канала. */
export const syncChannelPublications = async (postId: string) =>
	(await apiClient.post(`/admin/posts/${postId}/sync`)).data

/** Uploads a post's navigation-preview image via the full presign → S3 PUT → register flow. */
export const uploadNavigationPreview = async (
	postId: string,
	file: File,
): Promise<{ url: string }> => {
	const presign = await presignUpload(postId, [
		{ filename: file.name, mimeType: file.type, size: file.size, type: "preview" },
	])
	const upload = presign.uploads[0]

	await uploadFileToS3(upload.url, file)

	const response = await apiClient.post<{ url: string }>(`/admin/posts/${postId}/media/preview`, {
		s3Key: upload.s3Key,
		mimeType: file.type,
	})
	return response.data
}

/** Удаляет файл поста вместе с его обложкой. */
export const deletePostMedia = async (postId: string, mediaId: string): Promise<void> => {
	await apiClient.delete(`/admin/posts/${postId}/media/${mediaId}`)
}

/** Задаёт порядок вложений: первым идёт то, что откроется в альбоме первым. */
export const reorderPostMedia = async (postId: string, ids: string[]): Promise<Media[]> =>
	(await apiClient.patch<Media[]>(`/admin/posts/${postId}/media/order`, { ids })).data

/** Меняет обложку уже загруженного видео. */
export const setVideoPoster = async (
	postId: string,
	mediaId: string,
	file: File,
): Promise<Media[]> => {
	const presign = await presignUpload(postId, [
		{ filename: file.name, mimeType: file.type, size: file.size, type: "video_thumb" },
	])
	const upload = presign.uploads[0]
	await uploadFileToS3(upload.url, file)
	return (
		await apiClient.post<Media[]>(`/admin/posts/${postId}/media/${mediaId}/poster`, {
			s3Key: upload.s3Key,
			mimeType: file.type,
		})
	).data
}
